import Icon from '@forwardflow/ui/Icons'
import { PhoneNumberItem } from './PhoneNumberItem'
import { useEffect, useState, useMemo } from 'react'
import { SearchField } from '@forwardflow/ui/SearchField'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks/campaign'
import { useParams } from 'react-router-dom'
import { states } from '@forwardflow/helpers/utils/states'
import CampaignService from '@forwardflow/services/CampaignService'
import { message } from 'antd'
import { useGetAvailablePhoneNumbers } from '@forwardflow/hooks/customHooks/'
import { useCampaignPhoneNumberCountStore } from '@forwardflow/store/campaignPhoneNumberCountStore'
import { Spinner } from '@forwardflow/ui'
import { usePhoneNumberUnavailableModalStore } from '@forwardflow/store/modal/phoneNumberUnavailableModal'
import { IAvailablePhoneNumber } from '@forwardflow/types'

export const BuyPhoneNumber = () => {
   const [searchTerm, setSearchTerm] = useState('')
   const [isPaused, setIsPaused] = useState(false)
   const { title } = useParams()
   const { mutate: isLoadingActiveLeadSource } = useGetActiveLeadSource(
      title as string
   )
   const { data: LeadSource, mutate } = useGetActiveLeadSourceDetails(
      title as string
   )
   const { setCount } = useCampaignPhoneNumberCountStore()
   const {
      unavailablePhoneNumbers,
      isPhoneNumberUnavailableModalOpen,
      setUnavailablePhoneNumbers,
   } = usePhoneNumberUnavailableModalStore()

   const [edit, setEdit] = useState(false)

   const { data: phoneNumbers, isLoading } = useGetAvailablePhoneNumbers({
      limit: 100,
      search: searchTerm,
   })

   // Update pause status when modal is open
   useEffect(() => {
      setIsPaused(isPhoneNumberUnavailableModalOpen)
   }, [isPhoneNumberUnavailableModalOpen])

   // Filter out unavailable numbers from the LeadSource data
   const filteredAssignedPhoneNumbers = useMemo(() => {
      return (
         LeadSource?.assignedPhoneNumbers?.filter(
            (item: any) => !unavailablePhoneNumbers.includes(item.phoneNumber)
         ) || []
      )
   }, [LeadSource?.assignedPhoneNumbers, unavailablePhoneNumbers])

   useEffect(() => {
      setCount(filteredAssignedPhoneNumbers.length || 0)
   }, [filteredAssignedPhoneNumbers, setCount])

   // Filter region data without unavailable numbers
   const groupedByRegion = filteredAssignedPhoneNumbers.reduce(
      (acc: Record<string, any[]>, item: any) => {
         const region = item?.region || 'Unknown'
         if (!acc[region]) {
            acc[region] = []
         }
         acc[region].push(item)
         return acc
      },
      {} as Record<string, any[]>
   )

   const totalRegionCount = Object.keys(groupedByRegion || {}).length

   const onRemoveCampaignPhoneNumber = async (
      LeadSource: string,
      phoneNumber: string
   ) => {
      try {
         await CampaignService.removeCampaignPhoneNumber(
            LeadSource,
            phoneNumber
         )

         // Update the assigned phone numbers locally first
         mutate((currentData: any) => {
            return {
               ...currentData,
               assignedPhoneNumbers: currentData?.assignedPhoneNumbers?.filter(
                  (item: any) => item.phoneNumber !== phoneNumber
               ),
            }
         }, false)

         setUnavailablePhoneNumbers(
            unavailablePhoneNumbers.filter((num) => num !== phoneNumber)
         )

         const formattedNumber = formatPhoneNumber(phoneNumber) || phoneNumber
         message.success(`${formattedNumber} removed successfully`)
         isLoadingActiveLeadSource()
      } catch (error) {
         console.error('Failed to remove phone number:', error)
         const formattedNumber = formatPhoneNumber(phoneNumber) || phoneNumber
         message.error(`Failed to remove ${formattedNumber}`)
      }
   }

   const handlePhoneNumberSearch = (searchValue: string) => {
      setSearchTerm(searchValue)
   }

   const addCampaignPhoneNumbers = async (phoneNumberData = []) => {
      if (isPaused) return // Don't add numbers when system is paused

      try {
         await CampaignService.addCampaignPhoneNumber(
            title as string,
            phoneNumberData as any
         )
         await mutate(undefined, { revalidate: true })

         const phoneNumber =
            (phoneNumberData as any)?.friendlyName ||
            (phoneNumberData as any)?.phoneNumber ||
            'Phone number'
         message.success(`${phoneNumber} added successfully`)
      } catch (error) {
         console.error('Failed to add phone numbers:', error)

         const phoneNumber =
            (phoneNumberData as any)?.friendlyName ||
            (phoneNumberData as any)?.phoneNumber ||
            'Phone number'
         message.error(`Failed to add ${phoneNumber}`)
      }
   }

   const formatPhoneNumber = (phoneNumber: string) => {
      const cleaned = ('' + phoneNumber).replace(/\D/g, '')
      const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
      if (match) {
         return ['(', match[2], ') ', match[3], '-', match[4]].join('')
      }
      return null
   }

   return (
      <div className="flex flex-col flex-1">
         {/* Rest of your component remains the same */}
         {/* Just ensure to use filteredAssignedPhoneNumbers instead of LeadSource?.assignedPhoneNumbers */}
         <div className="flex items-center justify-between py-3 pl-10 pr-4">
            <span className="font-medium">Phone Numbers</span>
         </div>
         <div className="bg-primary/10">
            <div className="flex items-center px-10 py-5 font-medium bg-primary/10">
               <Icon name="Telephone" className="mr-4 scale-75 fill-white" />
               <span>{filteredAssignedPhoneNumbers.length}</span>

               <Icon name="Location" className="ml-auto mr-4 fill-white/70" />
               <span className="text-white/70">{totalRegionCount}</span>
            </div>
            {!edit && (
               <p className="text-[12px] text-[#B98BE6]/70 px-10 py-5">
                  We recommend you to add 3 numbers for optimal campaign
                  performance
               </p>
            )}
         </div>

         <>
            <div className="flex items-center bg-primary/40">
               {edit && (
                  <div
                     onClick={() => {
                        setEdit(false)
                        setSearchTerm('')
                     }}
                     className="border-r cursor-pointer border-white/20 px-7"
                  >
                     <Icon
                        name="arrow-right"
                        className="rotate-180"
                        width={20}
                     />
                  </div>
               )}
               <SearchField
                  placeholder="Search Phone numbers by area code or city"
                  className="!rounded-lg !text-[14px] !w-full !max-w-full !bg-transparent border-none"
                  onChange={handlePhoneNumberSearch}
                  handleClick={() => {
                     setEdit(true)
                  }}
                  value={searchTerm}
               />
            </div>
            {!edit && LeadSource?.assignedPhoneNumbers?.length === 0 && (
               <>
                  <div
                     onClick={() => {
                        setEdit(true)
                        setSearchTerm('719')
                     }}
                     className="py-5 cursor-pointer bg-primary/10 px-9 text-[14px] text-white/70 mt-2 flex items-center"
                  >
                     Search for numbers from 719 "Colorado Springs"
                     <Icon
                        name="arrow-down"
                        className="-rotate-90 opacity-70 ml-7"
                     />
                  </div>
                  <div
                     onClick={() => {
                        setEdit(true)
                        setSearchTerm('870')
                     }}
                     className="py-5 cursor-pointer bg-primary/10 px-9 text-[14px] text-white/70 mt-2 flex items-center"
                  >
                     Search for numbers for 870 "Western Colorado"
                     <Icon
                        name="arrow-down"
                        className="-rotate-90 opacity-70 ml-7"
                     />
                  </div>
               </>
            )}

            {searchTerm.length > 0 && edit && (
               <div className="flex justify-between text-[14px] px-10 py-5">
                  <span className="font-medium">
                     Showing results for "{searchTerm}"
                  </span>
                  <span className="text-white/70">
                     {searchTerm
                        ? phoneNumbers?.length
                        : phoneNumbers?.length || 0}{' '}
                     Number
                     {(phoneNumbers?.length || 0) !== 1 && 's'}
                  </span>
               </div>
            )}
            {/* list of available phone numbers */}

            {edit && unavailablePhoneNumbers.length > 0
               ? unavailablePhoneNumbers.map((number) => (
                    <PhoneNumberItem
                       key={number}
                       onRemoveCampaignPhoneNumber={() =>
                          onRemoveCampaignPhoneNumber(title as string, number)
                       }
                       number={formatPhoneNumber(number)!}
                       place="Unavailable"
                       isAdded={true}
                       hideActionButton={false}
                       isUnavailable={true}
                    />
                 ))
               : null}

            {edit &&
               phoneNumbers &&
               phoneNumbers?.map((item: IAvailablePhoneNumber) => (
                  <PhoneNumberItem
                     isAdded={false}
                     hideActionButton={edit}
                     number={item.friendlyName}
                     place={item.locality}
                     onAddCampaignPhoneNumber={() => {
                        addCampaignPhoneNumbers(item as never)
                     }}
                     onRemoveCampaignPhoneNumber={() =>
                        onRemoveCampaignPhoneNumber(
                           title as string,
                           item?.phoneNumber
                        )
                     }
                  />
               ))}

            {edit && isLoading && (
               <div className="flex justify-center py-5">
                  <Spinner />
               </div>
            )}
         </>

         {Object.entries(groupedByRegion || {}).map(([region, numbers]) => (
            <div key={region}>
               <div className="flex justify-between text-[14px] px-10 py-5">
                  <div className="flex items-center gap-x-2">
                     <span>
                        {
                           numbers[0]?.phoneNumber?.friendlyName?.match(
                              /\((\d+)\)/
                           )?.[1]
                        }
                     </span>
                     <span className="text-white/70">
                        {states.find((state) => state.value === region)
                           ?.label || region}
                     </span>
                  </div>
                  <span className="text-white/70">
                     {numbers.length} Number{numbers.length > 1 && 's'}
                  </span>
               </div>
               <div className="flex flex-col gap-y-1">
                  {numbers.map((item) => (
                     <PhoneNumberItem
                        key={item.phoneNumber}
                        onRemoveCampaignPhoneNumber={() =>
                           onRemoveCampaignPhoneNumber(
                              title as string,
                              item?.phoneNumber
                           )
                        }
                        isAdded={true}
                        hideActionButton={true}
                        number={item?.phoneNumber}
                        place={item?.locality}
                     />
                  ))}
               </div>
            </div>
         ))}
      </div>
   )
}
